import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgGridModule } from 'ag-grid-angular';
import { CellKeyDownEvent, FullWidthCellKeyDownEvent, ColDef, RowClickedEvent } from 'ag-grid-community';
import { Subject, catchError, of, switchMap, timer } from 'rxjs';
import { LaneCustomerPickerComponent } from '../../lane-routing-manager/lane-customer-picker.component';
import { LanePickerComponent } from '../../lane-routing-manager/lane-picker.component';
import { LaneCustomer, LaneSite } from '../../lane-routing-manager/lane.models';
import { GroupChoice } from '../relationship.models';
import { RoutingStopPickerComponent } from './routing-stop-picker.component';
import { RoutingStopCatalogue, RoutingStopInput, RoutingStopRow } from './routing-stop.models';
import { RoutingStopService } from './routing-stop.service';

@Component({
  selector: 'app-routing-stops',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, AgGridModule, LaneCustomerPickerComponent, LanePickerComponent, RoutingStopPickerComponent],
  templateUrl: './routing-stops.component.html',
  styleUrls: ['./routing-stops.component.scss']
})
export class RoutingStopsComponent implements OnInit {
  @Input() dark = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly groupSearches = new Subject<string>();
  private listRequest = 0;
  catalogue: RoutingStopCatalogue | null = null;
  rows: RoutingStopRow[] = [];
  selected: RoutingStopRow | null = null;
  customer: LaneCustomer | null = null;
  listCustomer: LaneCustomer | null = null;
  pickup: LaneSite | null = null;
  stop: LaneSite | null = null;
  group: GroupChoice | null = null;
  groupKind = '';
  groupQuery = '';
  groupResults: GroupChoice[] = [];
  groupBusy = false;
  search = '';
  offset = 0;
  total = 0;
  active = true;
  effectiveFrom = '';
  effectiveTo = '';
  notes = '';
  loading = false;
  saving = false;
  dirty = false;
  error = '';
  notice = '';

  readonly defaultColumn: ColDef<RoutingStopRow> = { resizable: true, sortable: true, minWidth: 120, flex: 1 };
  readonly gridText = { noRowsToShow: 'No routing-stop memberships match these filters.' };
  readonly columns: ColDef<RoutingStopRow>[] = [
    { field: 'customer_name', headerName: 'Customer', minWidth: 150 },
    { field: 'pickup_name', headerName: 'Pickup', minWidth: 140, tooltipField: 'pickup_street' },
    { field: 'stop_name', headerName: 'Warehouse / pool', minWidth: 160, tooltipField: 'stop_street' },
    { field: 'stop_city', headerName: 'City', minWidth: 100 },
    { headerName: 'Destination scope', minWidth: 140, valueGetter: params => this.scopeLabel(params.data) },
    { headerName: 'State', minWidth: 90, width: 100, flex: 0, valueGetter: params => this.stateLabel(params.data) }
  ];

  constructor(private readonly api: RoutingStopService) {}

  get canEdit(): boolean {
    return !!this.catalogue?.capabilities.edit;
  }

  get canSave(): boolean {
    return this.canEdit && !!this.catalogue?.configuration_ready && !this.saving
      && !!this.customer && !!this.pickup && !!this.stop && (!this.groupKind || !!this.group || this.canDisableStaleGroup);
  }

  private get canDisableStaleGroup(): boolean {
    return !this.active && !!this.selected && this.groupKind === this.selected.group_kind;
  }

  get hasMore(): boolean {
    return this.offset + this.rows.length < this.total;
  }

  ngOnInit(): void {
    this.groupSearches.pipe(
      switchMap(query => {
        if (query.length < 2 || !this.customer || !this.groupKind) return of({ data: [] as GroupChoice[] });
        const customer = this.customer.contact_uuid;
        const kind = this.groupKind;
        return timer(250).pipe(
          switchMap(() => this.api.groups(customer, kind, query)),
          catchError(error => { this.fail(error); return of({ data: [] as GroupChoice[] }); })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      this.groupBusy = false;
      this.groupResults = result.data;
    });
    this.api.catalogue().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: catalogue => { this.catalogue = catalogue; this.load(0); },
      error: error => this.fail(error)
    });
  }

  load(offset = this.offset): void {
    const request = ++this.listRequest;
    this.offset = offset;
    this.loading = true;
    this.error = '';
    this.api.list(this.search, this.listCustomer?.contact_uuid || '', offset).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: result => {
        if (request !== this.listRequest) return;
        this.rows = result.data;
        this.total = result.total;
        this.loading = false;
      },
      error: error => {
        if (request !== this.listRequest) return;
        this.loading = false;
        this.fail(error);
      }
    });
  }

  clearListCustomer(): void {
    this.listCustomer = null;
    this.load(0);
  }

  confirmDiscard(): boolean {
    return !this.dirty || window.confirm('Discard the unsaved routing-stop changes?');
  }

  newMembership(): void {
    if (this.saving || !this.confirmDiscard()) return;
    this.selected = null;
    this.stop = null;
    this.clearGroup();
    this.active = true;
    this.effectiveFrom = '';
    this.effectiveTo = '';
    this.notes = '';
    this.dirty = false;
    this.notice = '';
  }

  chooseCustomer(customer: LaneCustomer | null): void {
    if (!this.confirmDiscard()) return;
    this.customer = customer;
    this.listCustomer = customer;
    this.pickup = null;
    this.dirty = false;
    this.newMembership();
    this.load(0);
  }

  choosePickup(value: LaneCustomer | LaneSite | null): void {
    this.pickup = value && 'address_uuid' in value ? value : null;
    // A default pickup by itself is browsing, not an unsaved membership.
    if (this.selected || this.stop) this.changed();
  }

  chooseStop(stop: LaneSite | null): void {
    this.stop = stop;
    this.changed();
  }

  private clearGroup(): void {
    this.groupKind = '';
    this.group = null;
    this.groupQuery = '';
    this.groupResults = [];
    this.groupBusy = false;
    this.groupSearches.next('');
  }

  changeGroupKind(): void {
    this.group = null;
    this.searchGroups('');
    this.changed();
  }

  searchGroups(query: string): void {
    this.groupQuery = query;
    this.groupResults = [];
    this.groupBusy = query.length >= 2;
    this.groupSearches.next(query);
  }

  chooseGroup(group: GroupChoice | null): void {
    this.group = group;
    this.searchGroups('');
    this.changed();
  }

  changed(): void {
    this.dirty = true;
    this.notice = '';
  }

  openRow(event: RowClickedEvent<RoutingStopRow>): void {
    if (event.data) this.editMembership(event.data);
  }

  private editMembership(row: RoutingStopRow): void {
    if (this.saving || !this.confirmDiscard()) return;
    this.selected = row;
    this.customer = { contact_uuid: row.customer_contact_uuid, display_name: row.customer_name, active: null };
    this.pickup = { ...row.pickup, display_name: row.pickup_name, street: row.pickup_street, city: row.pickup_city, region: row.pickup_region, active: null };
    this.stop = { ...row.stop, display_name: row.stop_name, street: row.stop_street, city: row.stop_city, region: row.stop_region, active: null };
    this.clearGroup();
    this.groupKind = row.group_kind || '';
    this.group = row.group;
    this.active = row.active;
    this.effectiveFrom = row.effective_from || '';
    this.effectiveTo = row.effective_to || '';
    this.notes = row.notes;
    this.dirty = false;
    this.notice = '';
  }

  openWithKeyboard(event: CellKeyDownEvent<RoutingStopRow> | FullWidthCellKeyDownEvent<RoutingStopRow>): void {
    if (event.event instanceof KeyboardEvent && event.event.key === 'Enter' && event.data) {
      this.editMembership(event.data);
    }
  }

  save(): void {
    if (!this.canSave) return;
    const body: RoutingStopInput = {
      customer_contact_uuid: this.customer!.contact_uuid,
      pickup: this.identity(this.pickup!), stop: this.identity(this.stop!),
      group_kind: this.groupKind || null,
      group_uuid: this.group?.group_uuid || (this.canDisableStaleGroup ? this.selected!.group_uuid : null),
      active: this.active, effective_from: this.effectiveFrom || null, effective_to: this.effectiveTo || null, notes: this.notes
    };
    if (this.selected) body.expected_version = this.selected.version;
    this.saving = true;
    this.error = '';
    this.api.save(body, this.selected?.routing_stop_uuid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: result => {
        this.saving = false;
        this.selected = { ...this.selected, ...result.data };
        this.dirty = false;
        this.notice = 'Routing stop saved. Use Lane Routing Manager to define or approve its position in a route.';
        this.load();
      },
      error: error => this.fail(error)
    });
  }

  private identity(site: LaneSite): Pick<LaneSite, 'contact_uuid' | 'address_uuid'> {
    return { contact_uuid: site.contact_uuid, address_uuid: site.address_uuid };
  }

  scopeLabel(row?: RoutingStopRow): string {
    if (!row?.group_uuid) return 'All destinations';
    return row.group?.display_name || 'Group needs maintenance';
  }

  stateLabel(row?: RoutingStopRow): string {
    if (!row) return '';
    if (!row.active) return 'Disabled';
    if (!row.stop_valid || (row.group_uuid && !row.group)) return 'Needs maintenance';
    return 'Enabled';
  }

  private fail(error: { error?: { message?: string } }): void {
    this.saving = false;
    this.error = error.error?.message || 'The routing-stop request could not be completed.';
  }
}
